import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AppHeader,
  MainContent,
  FileList,
  AddLinkFAB,
  AddButton,
  type FileItemData,
} from '../components';
import { AddFileModal } from '../components/modals';
import { useAuth, useAppModals, useTheme, useDrawer, useMediaQuery } from '../hooks';
import { listFiles, searchFiles, getFile } from '../api/services';
import type { FilterPeriod } from '../components';

const transformFile = (raw: Record<string, unknown>): FileItemData => ({
  id: (raw.id as string) || '',
  name: (raw.name as string) || '',
  mimeType: (raw.mimeType as string) || (raw.mime_type as string) || 'application/octet-stream',
  fileType: (raw.fileType as 'document' | 'image' | 'audio') || 'document',
  fileSize: (raw.fileSize as number) ?? (raw.file_size as number) ?? 0,
  storagePath: (raw.storagePath as string) || (raw.storage_path as string) || '',
  thumbnailPath: (raw.thumbnailPath as string) ?? (raw.thumbnail_path as string) ?? null,
  createdAt: (raw.createdAt as string) || (raw.created_at as string) || new Date().toISOString(),
});

export const FilesScreen = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const [search, setSearch] = useState('');
  const [filterPeriod] = useState<FilterPeriod>('all');
  const [showAddModal, setShowAddModal] = useState(false);

  useTheme();
  const { isAuthenticated, isInitialized, email, fullName, avatarUrl } = useAuth();
  const { openAuthPrompt } = useAppModals();
  const { openDrawer } = useDrawer();

  const timezoneOffset = String(new Date().getTimezoneOffset());
  const isSearching = search.length >= 2;

  const filesQuery = useQuery({
    queryKey: ['files', filterPeriod],
    queryFn: () => listFiles({
      ...(filterPeriod !== 'all' && { period: filterPeriod }),
      timezoneOffset,
    }),
    enabled: isInitialized && !isSearching,
  });

  const searchQuery = useQuery({
    queryKey: ['files', 'search', search, filterPeriod],
    queryFn: () => searchFiles(search, {
      ...(filterPeriod !== 'all' && { period: filterPeriod }),
      timezoneOffset,
    }),
    enabled: isInitialized && isSearching,
  });

  const rawFiles = (
    isSearching ? searchQuery.data?.data ?? [] : filesQuery.data?.data ?? []
  ) as unknown as Record<string, unknown>[];

  const files: FileItemData[] = rawFiles.map(transformFile);
  const isLoading = isSearching ? searchQuery.isLoading : filesQuery.isLoading;
  const isRefetching = isSearching ? searchQuery.isFetching : filesQuery.isFetching;
  const error = isSearching ? searchQuery.error : filesQuery.error;

  const handleFilePress = async (file: FileItemData) => {
    try {
      const res = await getFile(file.id);
      const url = (res.data as { downloadUrl?: string })?.downloadUrl;
      if (url) window.open(url, '_blank');
    } catch {
      // ignore
    }
  };

  const handleMenuPress = () => {
    // TODO: FileMenuModal for delete/edit
  };

  const handleAddFileSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['files'] });
  };

  const handleAuthClick = () => {
    if (isAuthenticated) openDrawer();
    else openAuthPrompt();
  };

  return (
    <>
      <AppHeader
        isAuthenticated={isAuthenticated}
        fullName={fullName}
        email={email}
        avatarUrl={avatarUrl}
        onAuthClick={handleAuthClick}
        hideAuthOnDesktop={isDesktop}
      />
      <div style={{ padding: 'var(--space-md)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-lg)' }}>
          <h1 style={{ fontSize: 'var(--font-xl)', color: 'var(--color-text-primary)' }}>
            {t('files.title', 'Files')}
          </h1>
          {isDesktop && <AddButton onClick={() => setShowAddModal(true)} label={t('files.addFile', 'Add file')} />}
        </div>
        <input
          type="search"
          placeholder={t('files.searchPlaceholder', 'Search files...')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            width: '100%',
            padding: 'var(--space-sm) var(--space-md)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--color-border)',
            background: 'var(--color-bg-secondary)',
            color: 'var(--color-text-primary)',
          }}
        />
      </div>
      <MainContent>
        <FileList
          files={files}
          isLoading={isLoading}
          isRefetching={isRefetching}
          error={error ? String(error) : null}
          emptyTitle={t('files.noFilesYet', 'No files yet')}
          emptyDescription={t('files.tapToSaveFirst', 'Tap + to save your first file')}
          onFilePress={handleFilePress}
          onMenuPress={handleMenuPress}
        />
      </MainContent>
      {!isDesktop && <AddLinkFAB onClick={() => setShowAddModal(true)} />}
      <AddFileModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={handleAddFileSuccess}
      />
    </>
  );
};
